import { SKIMMER_CONFIG } from './config';

const { POSITIONING, SCALES, MODELS, BASE_HEIGHTS } = SKIMMER_CONFIG;

function calculateScaleFactor(value, baseValue, minScale) {
    if (value === null || value === undefined || value >= baseValue) return 1.0;
    return minScale + (1 - minScale) * (value / baseValue);
}

function getBaseScale(skimmerName) {
    if (!skimmerName) return SCALES.DEFAULT;
    const name = skimmerName.toString().toUpperCase();
    if (SCALES[name]) return SCALES[name];

    const match = name.match(/(EM\d{4})/);
    if (match && SCALES[match[1]]) return SCALES[match[1]];

    return SCALES.DEFAULT;
}

export function getBaseHeight(skimmerName) {
    if (!skimmerName) return BASE_HEIGHTS.DEFAULT;
    const name = skimmerName.toString().toUpperCase();
    if (BASE_HEIGHTS[name]) return BASE_HEIGHTS[name];

    const match = name.match(/(EM\d{4})/);
    if (match && BASE_HEIGHTS[match[1]]) return BASE_HEIGHTS[match[1]];

    return BASE_HEIGHTS.DEFAULT;
}

export function getWallOffset(skimmerName) {
    const { WALL_OFFSETS } = SKIMMER_CONFIG;
    if (!WALL_OFFSETS) return 0;
    if (!skimmerName) return WALL_OFFSETS.DEFAULT;

    const name = skimmerName.toString().toUpperCase();
    if (WALL_OFFSETS[name] !== undefined) return WALL_OFFSETS[name];

    const match = name.match(/(EM\d{4})/);
    if (match && WALL_OFFSETS[match[1]] !== undefined) return WALL_OFFSETS[match[1]];

    return WALL_OFFSETS.DEFAULT;
}

export const getSkimmerModelPath = (skimmerName) => {
    if (!skimmerName) return MODELS.DEFAULT;
    const name = skimmerName.toString().toUpperCase();

    if (MODELS[name]) return MODELS[name];

    if (name.includes('EM0010')) return MODELS.EM0010;
    if (name.includes('EM0020')) return MODELS.EM0020;
    if (name.includes('EM0030') || name.includes('EM0050')) return MODELS.EM0030;
    if (name.includes('EM0130')) return MODELS.EM0130;
    if (name.includes('EM0140')) return MODELS.EM0140;
    if (name.includes('EM0180')) return MODELS.EM0180;

    return MODELS.DEFAULT;
};

/**
 * Получить масштаб скиммера и компенсацию высоты
 */
export const getSkimmerModelParams = (skimmerName, depth, length, width, isLengthPositioning = false) => {
    const baseScale = getBaseScale(skimmerName);

    const depthScaleFactor = calculateScaleFactor(depth, POSITIONING.BASE_DEPTH, POSITIONING.MIN_DEPTH_SCALE);
    const lengthScaleFactor = calculateScaleFactor(length, POSITIONING.BASE_LENGTH, POSITIONING.MIN_LENGTH_SCALE);
    const widthScaleFactor = calculateScaleFactor(width, POSITIONING.BASE_WIDTH, POSITIONING.MIN_WIDTH_SCALE);

    const profDepthFactor = calculateScaleFactor(depth, POSITIONING.BASE_DEPTH, POSITIONING.PROJ_SCALE_MIN);
    const projLengthFactor = calculateScaleFactor(length, POSITIONING.BASE_LENGTH, POSITIONING.PROJ_SCALE_MIN);
    const projWidthFactor = calculateScaleFactor(width, POSITIONING.BASE_WIDTH, POSITIONING.PROJ_SCALE_MIN);
    const projectorStyleScale = Math.min(profDepthFactor, projLengthFactor, projWidthFactor);

    // Итоговые масштабы осей
    const finalScaleX = Math.min(depthScaleFactor, lengthScaleFactor, widthScaleFactor);
    const finalScaleY = Math.min(lengthScaleFactor, widthScaleFactor);
    const finalScaleZ = Math.min(lengthScaleFactor, widthScaleFactor);

    const scale = [
        baseScale[0] * finalScaleX * projectorStyleScale,
        baseScale[1] * finalScaleY * projectorStyleScale,
        baseScale[2] * finalScaleZ * projectorStyleScale
    ];

    // Расчет компенсации высоты
    const overallYScale = finalScaleY * projectorStyleScale;
    let heightCompensation = 0;
    const heightCompFactor = POSITIONING.HEIGHT_COMPENSATION_FACTOR ?? 1.0;

    if (overallYScale < 1.0) {
        const baseHeight = getBaseHeight(skimmerName);
        const heightReduction = baseHeight * (1 - overallYScale);
        // Положительное значение поднимает модель к верхнему борту.
        heightCompensation = heightReduction * heightCompFactor;
    }

    // Расчет компенсации отступа от стены / вылета в чашу
    // Делаем знак единым для всех стен: при уменьшении модели
    // чуть сильнее выдвигаем ее в бассейн.
    const overallXScale = finalScaleX * projectorStyleScale;
    let wallOffsetCompensation = 0;
    const maxWallComp = POSITIONING.WALL_PROTRUSION_COMPENSATION_MAX ?? 0;

    if (overallXScale < 1.0) {
        wallOffsetCompensation = -(1 - overallXScale) * maxWallComp;
    }

    // Протрузия (выступ) внутрь чаши
    const protrusionOffset = 0;

    return { scale, heightCompensation, wallOffsetCompensation, protrusionOffset };
};